/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import "./RateOrder.css";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { RateProduct } from "./RateProduct";
import { RateSeller } from "./RateSeller";
import { StarsRating } from "./StarsRating";
import useQuery from "./useQuery";
import { Ring } from "./Ring";
import { NpsScore } from "./NpsScore";
import { NetPromoterScoreSurvey } from "./NetPromoterScoreSurvey";
import { useTranslation } from "react-i18next";
import closeButton from "./close_button_inverse.png";

// Configuração: tipos de itens que devem ser movidos para o final quando inativos
// Opções: [] = nenhum, ['nps'] = apenas NPS, ['nps', 'store'] = NPS e loja, etc.
const MOVE_INACTIVE_TO_END = ['nps'];

export function RateOrder(props) {
  const { t, i18n } = useTranslation();
  const { external, preview } = props;
  const { customer, orderToken, orderId, sku: skuParam, previewId } = useParams();
  const query = useQuery();
  // Prioriza URL param, fallback para query string
  const sku = skuParam || query.get("sku");
  const embedded = query.get("embedded") === "1";

  const sendToParent = (type, payload = {}) => {
    if (embedded) window.parent.postMessage({ type, ...payload }, '*');
  };
  const [orderItems, setOrderItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [npsRating, setNpsRating] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isNpsModalVisible, setIsNpsModalVisible] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coupon, setCoupon] = useState(false);
  const [npsSurvey, setNpsSurvey] = useState(null);
  const [copyCoupon, setCopyCoupon] = useState(false);
  const [allowCoupon, setAllowCoupon] = useState(false);
  const [storeReview, setStoreReview] = useState([]);
  const [sellerReview, setSellerReview] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      setCopyCoupon(false);
    }, 5000);
  }, [copyCoupon]);

  const handleCopy = (e, code) => {
    e.preventDefault();
    setCopyCoupon(true);
    navigator.clipboard.writeText(code);
  };

  const getCouponMetadata = async () => {
    try {
      const source = query.get("source");
      const url = `${process.env.REACT_APP_API_URL}/${customer}/order/${orderId}/coupon-metadata${source ? `?source=${source}` : ''}`;
      
      var couponData = await axios.get(url);

      // Se retornar 204 (No Content), não há cupom elegível
      if (couponData.status === 204 || !couponData.data) {
        setCoupon(null);
        return;
      }

      // Armazena apenas os metadados (sem o código) para incentivo
      setCoupon(couponData.data);
    } catch (err) {
      console.error('Erro ao buscar metadados do cupom:', err);
      // Limpa cupom em caso de erro para evitar inconsistências
      setCoupon(null);
    }
  };

  const getOrderDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      var customerData = await axios.get(
        `${process.env.REACT_APP_API_URL}/${customer}/info`
      );

      // Verificar se os dados do cliente foram encontrados
      if (!customerData.data || customerData.data === null) {
        const error = new Error("Cliente não encontrado");
        error.response = { status: 404 };
        throw error;
      }

      setCustomerInfo(customerData.data);

      let orderDataUrl = "";
      if (preview && previewId) {
        orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/email-preview/${previewId}`;
      } else if (orderToken && orderToken !== "") {
        if (!external)
          orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/orders/${orderToken}/${orderId}`;
        else
          orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/external-orders/${orderToken}/${orderId}`;
      } else if (sku && sku !== "") {
        orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/orders/${orderId}/sku/${sku}`;
      }

      var orderData = await axios.get(orderDataUrl);
      
      // Verificar se o pedido foi encontrado
      if (!orderData.data || orderData.data === null) {
        const error = new Error("Pedido não encontrado");
        error.response = { status: 404 };
        throw error;
      }
      
      // Em preview, o endpoint retorna { template, orderData } — extraímos orderData
      const rawOrderData = (preview && orderData.data?.orderData) ? orderData.data.orderData : orderData.data;
      setOrderItems(rawOrderData);

      if (!preview && customerData.data.settings.embedNpsSurvey) {
        try {
          const npsData = await axios.get(
            `${
              process.env.REACT_APP_API_URL
            }/${customer}/nps/order/${orderId}?token=${orderToken ?? ""}&sku=${
              sku ?? ""
            }`
          );
          setNpsSurvey(npsData.data);
        } catch (npsErr) {
          console.error('Erro ao buscar pesquisa NPS:', npsErr);
          // Não definir erro global para NPS, apenas logar
        }
      }

      if (!preview && customerData.data.settings?.enableStoreReview && !external) {
        if (!rawOrderData.products[0]) {
          return;
        }

        setStoreReview({
          active: rawOrderData.storeReview === null ? true : false,
          isStore: true,
          customer: customer,
          logo: customerData.data.logo,
          orderId: orderId,
          sku: "store-reviews",
          name: customerData.data.name,
          _id: rawOrderData.products[0]._id,
          userId: rawOrderData.products[0].userId,
          rating: rawOrderData.storeReview?.rating || 0,
        });
      }

      // Só busca metadados se ainda não tiver um cupom completo (com código)
      if (!preview && (!coupon || !coupon.couponCode)) {
        await getCouponMetadata();
      }

      if (!firstLoaded) setFirstLoaded(true);
    } catch (err) {
      console.error('Erro ao buscar detalhes do pedido:', err);
      let errorMessage = t('error.order_fetch');
      
      if (err.response?.status === 404) {
        // Verificar se é erro de cliente ou pedido baseado na mensagem
        if (err.message?.includes('Cliente não encontrado')) {
          errorMessage = t('error.customer_not_found');
        } else {
          errorMessage = t('error.order_not_found');
        }
      } else if (err.response?.status === 403) {
        errorMessage = t('error.order_access_denied');
      } else if (err.response?.status >= 500) {
        errorMessage = t('error.server_error');
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para obter a ordem configurada
  const getConfiguredOrder = () => {
    return customerInfo?.settings?.collectionInterface?.collectionOrder || 
      (customerInfo?.settings?.storeReviewFirst 
        ? ['store', 'products', 'nps', 'sellers']
        : ['products', 'store', 'nps', 'sellers']);
  };

  // Função para obter todos os itens (ativos e inativos) na ordem configurada
  const getAllItems = () => {
    const allItems = [];
    const order = getConfiguredOrder();
    
    // Processar cada tipo na ordem configurada
    order.forEach(type => {
      switch(type) {
        case 'products':
          // Adiciona todos os produtos
          if (orderItems.products) {
            orderItems.products.forEach((item) => {
              allItems.push({ ...item, type: 'product' });
            });
          }
          break;
        
        case 'sellers':
          // Adiciona todos os sellers
          if (customerInfo?.settings?.enableSellerReview && orderItems.sellers) {
            orderItems.sellers.forEach((seller) => {
              allItems.push({ ...seller, type: 'seller' });
            });
          }
          break;
          
        case 'store':
          if (customerInfo?.settings?.enableStoreReview && !external && storeReview) {
            allItems.push({ ...storeReview, type: 'store' });
          }
          break;
          
        case 'nps':
          if (customerInfo?.settings?.embedNpsSurvey && npsSurvey) {
            allItems.push({ ...npsSurvey, type: 'nps' });
          }
          break;
          
        default:
          break;
      }
    });
    
    // Se houver tipos configurados para mover inativos para o final
    if (MOVE_INACTIVE_TO_END.length > 0) {
      // Separar itens que devem ser movidos quando inativos vs. os que mantêm posição
      const itemsToKeepInPlace = allItems.filter(item => 
        !MOVE_INACTIVE_TO_END.includes(item.type) || item.active
      );
      const inactiveItemsToMoveToEnd = allItems.filter(item => 
        MOVE_INACTIVE_TO_END.includes(item.type) && !item.active
      );
      
      return [...itemsToKeepInPlace, ...inactiveItemsToMoveToEnd];
    }
    
    // Retornar na ordem original se não houver configuração de reordenação
    return allItems;
  };

  // Função para obter todos os itens ativos
  const getActiveItems = () => {
    return getAllItems().filter(item => item.active);
  };

  // Função para obter o próximo item ativo
  const getNextActiveItem = (currentProduct) => {
    if (!currentProduct) return null;
    
    const activeItems = getActiveItems();
    
    // Encontra o índice do item atual
    let currentIndex = -1;
    
    for (let i = 0; i < activeItems.length; i++) {
      const item = activeItems[i];
      
      if (item.type === 'product' && currentProduct.sku === item.sku) {
        currentIndex = i;
        break;
      } else if (item.type === 'seller' && currentProduct.sellerId === item.sellerId) {
        currentIndex = i;
        break;
      } else if (item.type === 'store' && currentProduct.isStore) {
        currentIndex = i;
        break;
      } else if (item.type === 'nps' && currentProduct.type === 'nps') {
        currentIndex = i;
        break;
      }
    }
    
    // Se encontrou e há próximo item
    if (currentIndex >= 0 && currentIndex < activeItems.length - 1) {
      return activeItems[currentIndex + 1];
    }
    
    return null;
  };

  // Handler para navegar para o próximo item
  const handleNavigateToNext = async () => {
    const nextItem = getNextActiveItem(selectedProduct);
    
    if (nextItem) {
      if (nextItem.type === 'product' || nextItem.type === 'store') {
        setSelectedRating(null);
        setSelectedProduct(nextItem);
        setIsModalVisible(true);
        window.scrollTo(0, 0);
      } else if (nextItem.type === 'seller') {
        setIsModalVisible(false);
        setSellerReview(nextItem);
      } else if (nextItem.type === 'nps') {
        setIsModalVisible(false);
        setIsNpsModalVisible(true);
        window.scrollTo(0, 0);
      }
      
      // Recarrega pedido em background para atualizar status dos cards
      await getOrderDetails();
    } else {
      // Não há próximo item, recarrega e fecha o modal
      await getOrderDetails();
      setIsModalVisible(false);
      setSelectedProduct(null);
    }
  };

  // Função para renderizar um item baseado no tipo
  const renderItem = (item) => {
    switch(item.type) {
      case 'product':
        return (
          <div
            key={item.sku}
            className={`order-item ${item.active ? "" : "done"}`}
            onClick={() => {
              if (item.active) {
                setSelectedProduct(item);
                setIsModalVisible(true);
                window.scrollTo(0, 0);
              }
            }}
          >
            {!item.active && <div className="badge"></div>}
            <img
              className="picture"
              alt={t("alt.product_image", {
                name: item.product?.name ?? item.sku,
              })}
              src={
                item.product?.image
                  ? item.product.image
                  : "/image.jpeg"
              }
            />
            <div className="details">
              <div className="item-name">
                {item.product?.name ?? t("ref", { sku: item.sku })}
              </div>
              <div className="rating">
                <StarsRating
                  value={item.review ? item.review.rating : 0}
                  showText={false}
                  onClickDisabled={(value) => {
                    setSelectedRating(value);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'seller':
        return (
          <div
            key={item.sellerId}
            className={`order-item ${item.active ? "" : "done"}`}
            onClick={() => {
              if (item.active) {
                setSellerReview({ ...item });
                setIsModalVisible(false);
              }
            }}
          >
            {!item.active && <div className="badge"></div>}
            {item.logoUrl ? (
              <div className="picture" aria-label={t("alt.seller_logo", { name: item.displayName || item.sellerName })}>
                <img src={item.logoUrl} alt={item.displayName || item.sellerName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            ) : (
              <div className="picture monogram" aria-label={t("alt.seller_logo", { name: item.displayName || item.sellerName })}>
                {String(item.displayName || item.sellerName || "").trim().split(" ").slice(0,2).map(s => s[0]).join("")}
              </div>
            )}
            <div className="details">
              <div className="item-name">{t("rate_seller_experience", {
                storeName: item.displayName || item.sellerName,
              })}</div>
              <div className="rating">
                <StarsRating
                  value={item.review ? item.review.rating : 0}
                  onClickDisabled={(value) => {
                    setSelectedRating(value);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'store':
        return (
          <div
            key="store-review"
            className={`order-item store-review ${
              item.active ? "" : "done"
            }`}
            onClick={() => {
              if (item.active) {
                setSelectedProduct(item);
                setIsModalVisible(true);
                window.scrollTo(0, 0);
              }
            }}
          >
            {!item.active && <div className="badge"></div>}
            <img
              alt={t("alt.customer_logo", { name: customerInfo.name })}
              className="storeLogo"
              src={customerInfo.logo}
            />
            <div className="details">
              <div className="item-name">
                {t("rate_store_experience", {
                  storeName: customerInfo.name,
                })}
              </div>
              <div className="rating">
                <StarsRating
                  value={item.rating}
                  onClickDisabled={(value) => {
                    setSelectedRating(value);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'nps':
        return (
          <div
            key="nps-survey"
            className={`order-item nps ${
              item.active ? "" : "done"
            }`}
            onClick={() => {
              if (item.active) {
                setIsNpsModalVisible(true);
                window.scrollTo(0, 0);
              }
            }}
          >
            {!item.active && <div className="badge"></div>}
            <div className="details">
              <div className="item-name">
                {item.survey.npsQuestion}
              </div>
              <div className="rating">
                <NpsScore
                  value={-1}
                  onClickDisabled={(value) => {
                    setNpsRating(value);
                    setIsNpsModalVisible(true);
                  }}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    getOrderDetails();
  }, [orderToken, sku]);

  useEffect(() => {
    if (firstLoaded) {
      var ratingClicked = query.get("ratingClicked");
      var npsRating = query.get("npsRating");
      
      // Verifica se há rating clicked na query string
      if (ratingClicked) {
        var item = orderItems.products.find((x) => x.sku === sku);
        if (
          item &&
          item.active &&
          ratingClicked &&
          ratingClicked >= 1 &&
          ratingClicked <= 5
        ) {
          setSelectedProduct(item);
          setSelectedRating(ratingClicked);
          setTimeout(() => {
            setIsModalVisible(true);
          }, 500);
        }
      } else if (sku) {
        // SKU fornecido sem rating (ex: display=button): abre direto no produto
        var skuItem = orderItems.products.find((x) => x.sku === sku);
        if (skuItem && skuItem.active) {
          setSelectedProduct(skuItem);
          setTimeout(() => {
            setIsModalVisible(true);
            window.scrollTo(0, 0);
          }, 500);
        }
      } else {
        // Auto-abertura se houver apenas 1 item ativo
        const activeItems = getActiveItems();
        if (activeItems.length === 1) {
          const singleItem = activeItems[0];
          
          if (singleItem.type === 'product') {
            setSelectedProduct(singleItem);
            setTimeout(() => {
              setIsModalVisible(true);
              window.scrollTo(0, 0);
            }, 500);
          } else if (singleItem.type === 'seller') {
            setSellerReview(singleItem);
          } else if (singleItem.type === 'store') {
            setSelectedProduct(singleItem);
            setTimeout(() => {
              setIsModalVisible(true);
              window.scrollTo(0, 0);
            }, 500);
          }
        }
      }
    }

    if (npsRating) {
      setNpsRating(parseInt(npsRating));
      setTimeout(() => {
        setIsNpsModalVisible(true);
      }, 500);
    }
  }, [firstLoaded]);

  useEffect(() => {
    if (customerInfo?.settings?.locale) {
      i18n.changeLanguage(customerInfo.settings.locale);
    }
  }, [customerInfo]);

  useEffect(() => {
    if (!embedded) return;

    const notify = () => sendToParent('kfd:resize', { height: document.body.scrollHeight });
    notify();

    const observer = new ResizeObserver(notify);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [embedded]);

  return (
    <div className={`rate-order-page${embedded ? " embedded" : ""}`}>
      {preview && (
        <div style={{
          background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px',
          padding: '10px 16px', margin: '0 0 16px', fontFamily: 'sans-serif',
          fontSize: '13px', color: '#856404', textAlign: 'center',
        }}>
          <strong>⚠ Modo Pré-visualização</strong> — Nenhuma avaliação será registrada.
        </div>
      )}
      {customerInfo && (
        <>
          {customerInfo.importCssWebApp && (
            <link rel="stylesheet" href={customerInfo.importCssWebApp} />
          )}

          {embedded && !isModalVisible && !isNpsModalVisible && !sellerReview && (
            <button
              className="embedded-close-btn"
              onClick={() => sendToParent('kfd:close')}
              aria-label={t('close', { defaultValue: 'Fechar' })}
            >
              ×
            </button>
          )}

          <div className="logo">
            {customerInfo && (
              <img
                src={customerInfo.logo}
                alt={t("alt.customer_logo", { name: customerInfo.name })}
              />
            )}
          </div>
          {isLoading && (
            <div className="loading-container">
              <Ring />
              <p>{t('loading.order_details')}</p>
            </div>
          )}
          {error && (
            <div className="error-container">
              <div className="error-icon">
                <i className="bi bi-exclamation-triangle"></i>
              </div>
              <h2>{t('error.title')}</h2>
              <p>{error}</p>
              <button 
                onClick={() => {
                  setError(null);
                  getOrderDetails();
                }} 
                className="btn-retry"
              >
                {t('error.retry')}
              </button>
            </div>
          )}
          {embedded ? (
            // Modo embedded: troca de tela, sem modais sobrepostos dentro do iframe
            <>
              {!isLoading && !error && !isModalVisible && !isNpsModalVisible && !sellerReview && (
                <div className="order-items">
                  {getAllItems().map((item) => renderItem(item))}
                </div>
              )}
              {isModalVisible && (
                <RateProduct
                  key={`${selectedProduct?.sku}-${selectedProduct?.type}`}
                  show
                  embedded
                  preview={preview}
                  customerInfo={customerInfo}
                  product={selectedProduct}
                  external={external}
                  initialRating={selectedRating}
                  onHide={async () => {
                    if (!preview) await getOrderDetails();
                    setIsModalVisible(false);
                  }}
                  onComplete={(couponData) => {
                    if (preview) return;
                    setAllowCoupon(true);
                    sendToParent('kfd:review-sent');
                    if (couponData) setCoupon(couponData);
                  }}
                  getNextItem={() => getNextActiveItem(selectedProduct)}
                  onNavigateNext={handleNavigateToNext}
                />
              )}
              {!!sellerReview && (
                <RateSeller
                  show
                  embedded
                  preview={preview}
                  customer={customer}
                  customerInfo={customerInfo}
                  seller={sellerReview}
                  orderId={orderId}
                  onHide={() => setSellerReview(null)}
                  onComplete={async (couponData) => {
                    if (preview) return;
                    setSellerReview(null);
                    setAllowCoupon(true);
                    if (couponData) setCoupon(couponData);
                    await getOrderDetails();
                  }}
                />
              )}
              {isNpsModalVisible && (
                <NetPromoterScoreSurvey
                  show
                  embedded
                  preview={preview}
                  surveyData={npsSurvey}
                  customerData={customerInfo}
                  initialRating={npsRating}
                  onHide={() => {
                    if (!preview) getOrderDetails();
                    setNpsRating(-1);
                    setIsNpsModalVisible(false);
                  }}
                />
              )}
            </>
          ) : (
            // Modo padrão: lista de produtos + sub-formulários como modais sobrepostos
            <>
              {!isLoading && !error && (
                <>
                  <h3>
                    {getActiveItems().length > 0
                      ? (customerInfo.settings?.collectionInterface?.title || t("rate_order_title"))
                      : t("all_products_rated")}
                  </h3>
                  <div className="order-items">
                    {getAllItems().map((item) => renderItem(item))}
                  </div>
                </>
              )}
              <RateProduct
                key={`${selectedProduct?.sku}-${selectedProduct?.type}`}
                modal
                show={isModalVisible}
                preview={preview}
                customerInfo={customerInfo}
                product={selectedProduct}
                external={external}
                initialRating={selectedRating}
                onHide={async () => {
                  if (!preview) await getOrderDetails();
                  setIsModalVisible(false);
                }}
                onComplete={(couponData) => {
                  if (preview) return;
                  setAllowCoupon(true);
                  if (couponData) setCoupon(couponData);
                }}
                getNextItem={() => getNextActiveItem(selectedProduct)}
                onNavigateNext={handleNavigateToNext}
              />
              <RateSeller
                modal
                show={!!sellerReview}
                preview={preview}
                customer={customer}
                customerInfo={customerInfo}
                seller={sellerReview}
                orderId={orderId}
                onHide={() => setSellerReview(null)}
                onComplete={async (couponData) => {
                  if (preview) return;
                  setSellerReview(null);
                  setAllowCoupon(true);
                  if (couponData) setCoupon(couponData);
                  await getOrderDetails();
                }}
              />
              <NetPromoterScoreSurvey
                show={isNpsModalVisible}
                modal={true}
                preview={preview}
                surveyData={npsSurvey}
                customerData={customerInfo}
                initialRating={npsRating}
                onHide={() => {
                  if (!preview) getOrderDetails();
                  setNpsRating(-1);
                  setIsNpsModalVisible(false);
                }}
              />
            </>
          )}

          {allowCoupon && coupon && (
            <div className="coupon-div">
              
              <div className="coupon-text">
                <button className="close-coupon" onClick={() => {
                  setCoupon(null);
                  setAllowCoupon(false);
                }}>
                <img alt={t('alt.close')} src={closeButton} />
              </button>
                <p className="line1">
                  {t("coupon_received", {
                    discount: coupon.isPercentage
                      ? `${coupon.discountPercentage}%`
                      : coupon.discountPercentage,
                  })}
                </p>
                <p className="line2">
                  {t("coupon_use_code", { code: coupon.couponCode })}
                </p>
                <p className="line3">
                  {t("coupon_validity", { days: coupon.couponValidity })}
                </p>
                <p className="line4">
                  {!copyCoupon && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="icone"
                      >
                        {" "}
                        <polygon
                          fill="var(--ci-primary-color, currentColor)"
                          points="408 432 376 432 376 464 112 464 112 136 144 136 144 104 80 104 80 496 408 496 408 432"
                          class="ci-primary"
                        />{" "}
                        <path
                          fill="var(--ci-primary-color, currentColor)"
                          d="M176,16V400H496V153.373L358.627,16ZM464,368H208V48H312V200H464Zm0-200H344V48h1.372L464,166.627Z"
                          class="ci-primary"
                        />{" "}
                      </svg>
                      <button
                        className="link-button"
                        onClick={(event) => handleCopy(event, coupon.couponCode)}
                      >
                        {t("copy_code")}
                      </button>
                    </>
                  )}
                  {copyCoupon && t("code_copied")}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
